import { Router } from "@angular/router";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Observable } from "rxjs";
import { debounceTime, filter, map, take } from "rxjs/operators";

import { OnboardingCentredModalComponent } from "@common/onboarding-centred-modal/onboarding-centred-modal.component";

/**
 * Shared mechanics for the onboarding lessons shown as a centred modal - H01 (FLIP-334) over Home,
 * S01 (FLIP-339) over the Score landing page. Plain functions over MatDialog
 * and OnboardingCentredModalComponent; each lesson service keeps its own eligibility, state writes
 * and tracking.
 */

export const HOME_ROUTE = "/landing-page";

/**
 * How long the blocking-UI signal must stay quiet before a Home lesson opens, so it lands after a
 * product modal closes rather than in the gap between one closing and the next opening.
 */
export const ONBOARDING_QUIET_PERIOD_MS = 1000;

/**
 * How the modal was exited. "dismiss" is the standard dismissal - the close X, Escape, and
 * MatDialog closing itself on navigation (Android Back). What a dismissal means (complete for an
 * informational lesson, defer for a task) is the lesson's call.
 */
export type OnboardingModalExit = "primary" | "secondary" | "dismiss";

export interface OnboardingModalContent {
  heading: string;
  body: string;
  /** A second paragraph in the same view - e.g. P12's core-completion sentence (FLIP-362). */
  bodySecondary?: string;
  primaryLabel?: string;
  secondaryLabel?: string;
}

/** The centred modal's heading - where focus lands on open (see openOnboardingCentredModal). */
export const ONBOARDING_MODAL_HEADING_SELECTOR = ".ob-modal__heading";

/**
 * Opens OnboardingCentredModalComponent directly via MatDialog - it's the one genuine modal of the
 * onboarding cards (see its doc comment). The backdrop is transparent (no dark overlay) but still
 * blocks the page behind; disableClose so a tap on it does nothing, with Escape re-enabled as a
 * standard dismissal. Emits once, on close.
 *
 * Focus (component contract): moves to the heading on open (it carries tabindex="-1"), rather than
 * the first tabbable element - the close X; MatDialog's focus trap keeps it inside and restoreFocus
 * (on by default) returns it to the invoking control on close.
 */
export function openOnboardingCentredModal(
  dialog: MatDialog,
  content: OnboardingModalContent
): Observable<OnboardingModalExit> {
  return openOnboardingCentredModalWithRef(dialog, content).exit$;
}

/**
 * openOnboardingCentredModal, also returning the dialog ref - for a lesson that must close its own
 * modal (e.g. the user leaves the page) without that counting as an exit.
 */
export function openOnboardingCentredModalWithRef(
  dialog: MatDialog,
  content: OnboardingModalContent
): { ref: MatDialogRef<OnboardingCentredModalComponent>; exit$: Observable<OnboardingModalExit> } {
  const ref = dialog.open(OnboardingCentredModalComponent, {
    panelClass: "base-modal-panel",
    backdropClass: "onboarding-modal-backdrop",
    disableClose: true,
    autoFocus: ONBOARDING_MODAL_HEADING_SELECTOR,
    restoreFocus: true,
  });
  const modal = ref.componentInstance;
  modal.heading = content.heading;
  modal.bodyPrimary = content.body;
  modal.bodySecondary = content.bodySecondary;
  modal.primaryLabel = content.primaryLabel;
  modal.secondaryLabel = content.secondaryLabel;

  // Stays "dismiss" unless a button set it.
  let exit: OnboardingModalExit = "dismiss";
  modal.primaryAction.subscribe(() => {
    exit = "primary";
    ref.close();
  });
  modal.secondaryAction.subscribe(() => {
    exit = "secondary";
    ref.close();
  });
  modal.closed.subscribe(() => ref.close());
  ref
    .keydownEvents()
    .pipe(filter((event) => event.key === "Escape"))
    .subscribe(() => ref.close());

  return {
    ref,
    exit$: ref.afterClosed().pipe(
      take(1),
      map(() => exit)
    ),
  };
}

/** Emits once, when no blocking product modal/snackbar has been open for the quiet period. */
export function whenUiQuiet(isBlockingUiActive$: Observable<boolean>): Observable<boolean> {
  return isBlockingUiActive$.pipe(
    debounceTime(ONBOARDING_QUIET_PERIOD_MS),
    filter((isBlocking) => !isBlocking),
    take(1)
  );
}

/** Whether the current URL (ignoring query and fragment) is exactly `route`. */
export function isOnRoute(router: Router, route: string): boolean {
  return router.url.split(/[?#]/)[0] === route;
}

export function isOnHome(router: Router): boolean {
  return isOnRoute(router, HOME_ROUTE);
}
